import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { surveyService } from '../../services/surveyService';
import { surveyCategoryService } from '../../services/surveyCategoryService';
import { questionsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FolderIcon, 
  DocumentTextIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  EllipsisVerticalIcon,
  QuestionMarkCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const SurveyCategoryManager = () => {
  const { isSuperAdmin, isCompanyAdmin, isAuthenticated } = useAuth();
  const canManage = isSuperAdmin || isCompanyAdmin;
  const navigate = useNavigate();
  const location = useLocation();
  
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryQuestions, setCategoryQuestions] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState({});
  const [error, setError] = useState('');
  const [actionDropdown, setActionDropdown] = useState(null);
  const dropdownRefs = useRef({});
  
  // Search & Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'withQuestions', 'withoutQuestions'
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [questionPages, setQuestionPages] = useState({});

  // Check for preselected survey from navigation state
  useEffect(() => {
    const navigationState = location.state;
    if (navigationState?.selectedSurveyId) {
      // Load surveys and automatically select the one from navigation
      const loadWithPreselectedSurvey = async () => {
        try {
          setLoading(true);
          const surveysData = await surveyService.getSurveys();
          setSurveys(surveysData);
          
          const survey = surveysData.find(s => s.id === navigationState.selectedSurveyId);
          if (survey) {
            setSelectedSurvey(survey);
            await loadCategories(survey.id);
          } else {
            setError('Selected survey not found');
          }
        } catch (err) {
          setError('Failed to load surveys: ' + err.message);
        } finally {
          setLoading(false);
        }
      };
      
      if (isAuthenticated) {
        loadWithPreselectedSurvey();
      }
    } else {
      // Normal load without preselected survey
      if (isAuthenticated) {
        loadSurveys();
      }
    }
  }, [isAuthenticated, location.state]);

  const loadSurveys = async () => {
    try {
      setLoading(true);
      const data = await surveyService.getSurveys();
      setSurveys(data);
    } catch (err) {
      setError('Failed to load surveys: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async (surveyId) => {
    try {
      const categoriesData = await surveyCategoryService.getSurveyCategories(surveyId);
      setCategories(categoriesData || []);
      // Reset questions and expanded states
      setCategoryQuestions({});
      setExpandedCategories({});
    } catch (err) {
      setError('Failed to load categories: ' + err.message);
      setCategories([]);
    }
  };

  const handleSurveyChange = async (surveyId) => {
    if (!surveyId) {
      setSelectedSurvey(null);
      setCategories([]);
      setCategoryQuestions({});
      setExpandedCategories({});
      // Clear navigation state when changing survey
      navigate('/survey-categories-manager', { replace: true });
      return;
    }

    try {
      setLoading(true);
      const survey = surveys.find(s => s.id === parseInt(surveyId));
      setSelectedSurvey(survey);
      await loadCategories(surveyId);
    } catch (err) {
      setError('Failed to load categories: ' + err.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryQuestions = async (categoryId) => {
    try {
      setQuestionsLoading(prev => ({ ...prev, [categoryId]: true }));
      const response = await questionsAPI.getByCategory(categoryId);
      setCategoryQuestions(prev => ({
        ...prev,
        [categoryId]: response.data || []
      }));
      
      // Initialize pagination for this category's questions
      setQuestionPages(prev => ({
        ...prev,
        [categoryId]: 1
      }));
    } catch (err) {
      console.error(`Failed to load questions for category ${categoryId}:`, err);
      setCategoryQuestions(prev => ({
        ...prev,
        [categoryId]: []
      }));
    } finally {
      setQuestionsLoading(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));

    // Load questions if not already loaded
    if (!expandedCategories[categoryId] && !categoryQuestions[categoryId]) {
      loadCategoryQuestions(categoryId);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete "${categoryName}"? This cannot be undone.`)) return;
    try {
      setLoading(true);
      await surveyCategoryService.deleteSurveyCategory(categoryId);
      if (selectedSurvey) {
        const categoriesData = await surveyCategoryService.getSurveyCategories(selectedSurvey.id);
        setCategories(categoriesData || []);
        // Remove from local state
        setCategoryQuestions(prev => {
          const newState = { ...prev };
          delete newState[categoryId];
          return newState;
        });
        setExpandedCategories(prev => {
          const newState = { ...prev };
          delete newState[categoryId];
          return newState;
        });
      }
    } catch (err) {
      setError('Failed to delete category: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    if (selectedSurvey) {
      navigate('/survey-categories', { 
        state: { 
          mode: 'edit', 
          surveyId: selectedSurvey.id,
          categoryToEdit: category 
        } 
      });
    }
    setActionDropdown(null);
  };

  const handleCreateCategories = () => {
    if (selectedSurvey) {
      navigate('/survey-categories', { 
        state: { 
          mode: 'create', 
          surveyId: selectedSurvey.id 
        } 
      });
    }
  };

  const handleManageQuestions = (category) => {
    if (selectedSurvey) {
      navigate(`/surveys/${selectedSurvey.id}/categories/${category.id}/questions`);
    }
    setActionDropdown(null);
  };

  const handleBackToSurveys = () => {
    navigate('/surveys');
  };

  const handleActionClick = (categoryId, e) => {
    e.stopPropagation();
    setActionDropdown(actionDropdown === categoryId ? null : categoryId);
  };

  // Search and filter logic
  const filteredCategories = useMemo(() => {
    let filtered = categories;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (categoryQuestions[category.id]?.some(question => 
          question.questionText?.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
    }

    // Apply question filter
    if (filterType === 'withQuestions') {
      filtered = filtered.filter(category => 
        categoryQuestions[category.id]?.length > 0
      );
    } else if (filterType === 'withoutQuestions') {
      filtered = filtered.filter(category => 
        !categoryQuestions[category.id] || categoryQuestions[category.id].length === 0
      );
    }

    return filtered;
  }, [categories, searchTerm, filterType, categoryQuestions]);

  // Pagination logic for categories
  const totalItems = filteredCategories.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCategories = filteredCategories.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    document.getElementById('categories-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  // Pagination for questions within each category
  const handleQuestionPageChange = (categoryId, page) => {
    setQuestionPages(prev => ({
      ...prev,
      [categoryId]: page
    }));
  };

  const getQuestionsForPage = (categoryId, questions, page, perPage = 3) => {
    if (!questions) return [];
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return questions.slice(startIndex, endIndex);
  };

  const getTotalQuestionPages = (questions, perPage = 3) => {
    if (!questions) return 0;
    return Math.ceil(questions.length / perPage);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setFilterType('all');
    setCurrentPage(1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideDropdown = false;
      
      Object.keys(dropdownRefs.current).forEach(key => {
        const ref = dropdownRefs.current[key];
        if (ref && ref.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setActionDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset states when survey changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
    setFilterType('all');
  }, [selectedSurvey]);

  if (!isAuthenticated) {
    return (
      <div className="p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen flex items-center justify-center">
        <h2 className="text-xl font-semibold text-gray-900">Please log in to view categories</h2>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen">
      {/* Header with Back Button */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {location.state?.fromSurveyList && (
              <button
                onClick={handleBackToSurveys}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Surveys"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Survey Categories Manager</h1>
              <p className="text-gray-600">
                {location.state?.fromSurveyList 
                  ? `Managing categories for: ${location.state.selectedSurveyName || selectedSurvey?.name || 'Selected Survey'}`
                  : 'Select a survey to view and manage its categories'
                }
              </p>
            </div>
          </div>
          
          {location.state?.fromSurveyList && (
            <button
              onClick={() => navigate('/survey-categories-manager', { replace: true })}
              className="px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              Change Survey
            </button>
          )}
        </div>
      </div>

      {/* Survey Selection */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Survey
            </label>
            <select
              value={selectedSurvey?.id || ''}
              onChange={(e) => handleSurveyChange(e.target.value)}
              className="w-full lg:max-w-md px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              disabled={loading}
            >
              <option value="">Choose a survey...</option>
              {surveys.map(survey => (
                <option key={survey.id} value={survey.id}>
                  {survey.name} - {survey.company?.name}
                </option>
              ))}
            </select>
          </div>

          {selectedSurvey && canManage && (
            <button 
              onClick={handleCreateCategories}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium shadow-sm flex items-center gap-2 whitespace-nowrap transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Create Categories
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {selectedSurvey && !loading && (
        <div id="categories-section" className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6">
          {/* Search and Filter Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Categories for: {selectedSurvey.name}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {filteredCategories.length} of {categories.length} categories
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              {/* Search Input */}
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search categories or questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-64 transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Filter Dropdown */}
              <div className="relative">
                <FunnelIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-48 appearance-none bg-white transition-colors"
                >
                  <option value="all">All Categories</option>
                  <option value="withQuestions">With Questions</option>
                  <option value="withoutQuestions">Without Questions</option>
                </select>
              </div>
            </div>
          </div>

          {/* Categories List */}
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <FolderIcon className="w-8 h-8 text-blue-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-3">
                {searchTerm || filterType !== 'all' 
                  ? 'No categories match your search criteria.' 
                  : 'No categories found.'
                }
              </p>
              {canManage && (
                <button
                  onClick={handleCreateCategories}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium flex items-center gap-2 mx-auto transition-colors"
                >
                  <PlusIcon className="w-5 h-5" />
                  Create Categories
                </button>
              )}
              {(searchTerm || filterType !== 'all') && (
                <button
                  onClick={clearSearch}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 mt-2 transition-colors"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {currentCategories.map((category) => (
                <div 
                  key={category.id}
                  className="border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200"
                >
                  {/* Category Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 flex justify-between items-center transition-colors"
                    onClick={() => toggleCategoryExpansion(category.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <FolderIcon className="w-5 h-5 text-blue-500" />
                        <div>
                          <h3 className="font-semibold text-gray-900">{category.name}</h3>
                          <p className="text-sm text-gray-500">
                            {category.description || 'No description'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <QuestionMarkCircleIcon className="w-4 h-4" />
                          {categoryQuestions[category.id]?.length || 0} questions
                        </span>
                        <span>Order: {category.order}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategoryExpansion(category.id);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title={expandedCategories[category.id] ? "Collapse" : "Expand"}
                      >
                        {expandedCategories[category.id] ? (
                          <EyeSlashIcon className="w-5 h-5" />
                        ) : (
                          <EyeIcon className="w-5 h-5" />
                        )}
                      </button>

                      {canManage && (
                        <div
                          className="relative"
                          ref={el => { dropdownRefs.current[category.id] = el; }}
                        >
                          <button
                            onClick={(e) => handleActionClick(category.id, e)}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Actions"
                          >
                            <EllipsisVerticalIcon className="w-5 h-5" />
                          </button>
                          
                          {actionDropdown === category.id && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-48">
                              <button
                                onClick={() => handleEdit(category)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                                Edit Category
                              </button>
                              <button
                                onClick={() => handleManageQuestions(category)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 transition-colors"
                              >
                                <PlusIcon className="w-4 h-4" />
                                Manage Questions
                              </button>
                              <button
                                onClick={() => handleDelete(category.id, category.name)}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                                Delete Category
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded Questions Section */}
                  {expandedCategories[category.id] && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                      {questionsLoading[category.id] ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-2">Loading questions...</p>
                        </div>
                      ) : categoryQuestions[category.id]?.length > 0 ? (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                            <DocumentTextIcon className="w-4 h-4" />
                            Questions ({categoryQuestions[category.id].length})
                          </h4>
                          
                          {/* Questions List with Pagination */}
                          <div className="space-y-2 mb-4">
                            {getQuestionsForPage(
                              category.id, 
                              categoryQuestions[category.id], 
                              questionPages[category.id] || 1
                            ).map((question, index) => (
                              <div 
                                key={question.id}
                                className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-sm transition-shadow"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      {question.questionText}
                                    </p>
                                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                                      <span>Type: {question.type}</span>
                                      <span>Required: {question.isRequired ? 'Yes' : 'No'}</span>
                                      <span>Order: {question.order}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Questions Pagination */}
                          {getTotalQuestionPages(categoryQuestions[category.id]) > 1 && (
                            <div className="flex justify-between items-center mt-4">
                              <button
                                onClick={() => handleQuestionPageChange(
                                  category.id, 
                                  Math.max(1, (questionPages[category.id] || 1) - 1)
                                )}
                                disabled={(questionPages[category.id] || 1) === 1}
                                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                              >
                                Previous
                              </button>
                              
                              <span className="text-sm text-gray-600">
                                Page {questionPages[category.id] || 1} of {getTotalQuestionPages(categoryQuestions[category.id])}
                              </span>
                              
                              <button
                                onClick={() => handleQuestionPageChange(
                                  category.id, 
                                  Math.min(
                                    getTotalQuestionPages(categoryQuestions[category.id]), 
                                    (questionPages[category.id] || 1) + 1
                                  )
                                )}
                                disabled={(questionPages[category.id] || 1) === getTotalQuestionPages(categoryQuestions[category.id])}
                                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <QuestionMarkCircleIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p>No questions found for this category.</p>
                          {canManage && (
                            <button
                              onClick={() => handleManageQuestions(category)}
                              className="mt-2 px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              Add Questions
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Categories Pagination */}
          {filteredCategories.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
                <span className="text-sm text-gray-700">categories per page</span>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages} • {totalItems} total categories
                </span>
                
                <div className="flex gap-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === totalPages || 
                      Math.abs(page - currentPage) <= 1
                    )
                    .map((page, index, array) => {
                      const showEllipsis = index < array.length - 1 && array[index + 1] - page > 1;
                      return (
                        <React.Fragment key={page}>
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 text-sm border rounded transition-colors ${
                              currentPage === page
                                ? 'bg-blue-500 text-white border-blue-500'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                          {showEllipsis && (
                            <span className="px-2 text-gray-500">...</span>
                          )}
                        </React.Fragment>
                      );
                    })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading...</span>
        </div>
      )}
    </div>
  );
};

export default SurveyCategoryManager;